// Начертание изолиний механизмов: слои MapLibre и правило штриха.
//
// ЗНАК ЧИТАЕТСЯ ШТРИХОМ, А НЕ ЦВЕТОМ. Цвет здесь уже занят СЕМЕЙСТВОМ (их до
// шести одновременно), и добавить к шести цветам ещё по знаку — двенадцать
// различимых линий поверх растра, чего не бывает. Штрих вдобавок переживает
// печать, чёрно-белую копию и дальтонизм, а colorExpr — нет.
//
// `line-dasharray` в MapLibre НЕ data-driven, поэтому штриховой вариант — это
// отдельный слой с фильтром `level < 0`. Тот же приём, что у спорных рубежей
// ИЭЗ (`map/eezLayer.ts`).

export const UPW_ISO_PREFIX = 'upw-iso-'

export type UpwIsoPart = 'casing' | 'solid' | 'dashed' | 'zero' | 'label'

export function upwIsoSourceId(mechanism: string): string {
  return `${UPW_ISO_PREFIX}src-${mechanism}`
}

export function upwIsoLabelSourceId(mechanism: string): string {
  return `${UPW_ISO_PREFIX}lbl-src-${mechanism}`
}

export function upwIsoLayerId(mechanism: string, part: UpwIsoPart): string {
  return `${UPW_ISO_PREFIX}${mechanism}-${part}`
}

/** Белая обводка под линией — приём читаемости поверх ЧУЖОЙ картинки (растра
 *  механизма), а не айдентика: общий на все бренды, как у изотерм. */
export const UPW_CASING_COLOR = '#ffffff'
export const UPW_CASING_OPACITY = 0.6

const WIDTH = 1.6
const CASING_EXTRA = 2.2

/** Толщина линии по зуму: на обзорном тоньше, иначе семейство сливается в пятно. */
export function upwWidthExpr(width: number) {
  return ['interpolate', ['linear'], ['zoom'], 3, width * 0.7, 7, width, 11, width * 1.4]
}

/** Штрих отрицательной ветви. Короткий и частый — чтобы читался и на коротких
 *  обрывках, которыми marching squares отдаёт геометрию. */
export const UPW_DASH: [number, number] = [1.6, 1.4]

/** Нулевая линия толще прочих и всегда сплошная: это не «ещё один уровень», а
 *  ГРАНИЦА явления, и она обязана читаться первой. */
export const UPW_ZERO_WIDTH = WIDTH * 1.7

/** Описания слоёв одного механизма, снизу вверх по стопке. */
export function upwIsoLayerSpecs(mechanism: string) {
  const src = upwIsoSourceId(mechanism)
  const colorExpr = ['get', 'color'] as unknown
  return [
    {
      id: upwIsoLayerId(mechanism, 'casing'),
      type: 'line' as const,
      source: src,
      paint: {
        'line-color': UPW_CASING_COLOR,
        'line-opacity': UPW_CASING_OPACITY,
        'line-width': upwWidthExpr(WIDTH + CASING_EXTRA),
      },
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
    },
    {
      id: upwIsoLayerId(mechanism, 'solid'),
      type: 'line' as const,
      source: src,
      // Ноль рисуется своим слоем — иначе он был бы просто самой обычной из
      // линий, тогда как весь вопрос раздела «где эта граница».
      filter: ['all', ['!', ['get', 'zero']], ['>=', ['get', 'level'], 0]],
      paint: { 'line-color': colorExpr, 'line-width': upwWidthExpr(WIDTH) },
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
    },
    {
      id: upwIsoLayerId(mechanism, 'dashed'),
      type: 'line' as const,
      source: src,
      filter: ['all', ['!', ['get', 'zero']], ['<', ['get', 'level'], 0]],
      paint: {
        'line-color': colorExpr,
        'line-width': upwWidthExpr(WIDTH),
        'line-dasharray': UPW_DASH,
      },
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
    },
    {
      id: upwIsoLayerId(mechanism, 'zero'),
      type: 'line' as const,
      source: src,
      filter: ['get', 'zero'],
      paint: { 'line-color': colorExpr, 'line-width': upwWidthExpr(UPW_ZERO_WIDTH) },
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
    },
  ]
}
